"use client"

import { Popover, Transition } from "@headlessui/react"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { StoreRegion } from "@medusajs/types"
import { Text, clx, useToggleState } from "@medusajs/ui"
import { Fragment } from "react"

import CountrySelect from "../country-select"
import { Menu } from "lucide-react"
import { AuthButtons } from "./AuthButtons"
import { BottomNavLinks } from "@/types/global"
import Link from "next/link"

type SideMenuProps = {
  regions: StoreRegion[] | null
  bottom_nav_links: BottomNavLinks[]
}

const SideMenu = ({ regions, bottom_nav_links }: SideMenuProps) => {
  const toggleState = useToggleState()

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className="relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none hover:text-ui-fg-base"
                >
                  <Menu className="w-6 h-6" />
                </Popover.Button>
              </div>

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <Popover.Panel className="rounded-rounded flex flex-col absolute sm:pr-0 sm:w-1/3 2xl:w-1/4 sm:min-w-min h-[calc(100vh-1rem)] z-50 inset-y-0 right-0 text-sm text-ui-fg-on-color m-2 backdrop-blur-2xl">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex flex-col h-full bg-[rgba(3,7,18,0.5)] rounded-rounded justify-between p-6"
                  >
                    <div
                      className="flex justify-between items-center"
                      id="xmark"
                    >
                      {/* <ThemeToggle /> */}
                      <button data-testid="close-menu-button" onClick={close}>
                        <XMark />
                      </button>
                    </div>

                    <ul className="flex flex-col gap-6 items-start justify-start">
                      {bottom_nav_links.map((item, index) => {
                        return (
                          <li key={`${item.label}-${index}`}>
                            <Link
                              href={item.url}
                              className="text-3xl leading-10 hover:text-ui-fg-disabled"
                              onClick={close}
                              data-testid={`${item.label.toLowerCase()}-link`}
                            >
                              {item.label}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                    <div>
                      {/* login buttons */}
                      <AuthButtons />

                      <div className="flex flex-col gap-y-6">
                        <div
                          className="flex justify-between"
                          onMouseEnter={toggleState.open}
                          onMouseLeave={toggleState.close}
                        >
                          {regions && (
                            <CountrySelect
                              toggleState={toggleState}
                              regions={regions}
                            />
                          )}
                          <ArrowRightMini
                            className={clx(
                              "transition-transform duration-150",
                              toggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                        <Text className="txt-compact-small">
                          © {new Date().getFullYear()} <strong>101yapi</strong>. All Rights Reserved.
                        </Text>
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
